import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Wifi, Car, Users, Zap } from 'lucide-react';
import { useReveal, useParallax } from '../hooks/useReveal';
import { useLanguage } from '../context/LanguageContext';
import SlideArrow from '../components/SlideArrow';

/* ─── shared helpers ─────────────────────────────────────────── */

const GridBackdrop = ({ className = '' }) => (
  <div
    aria-hidden="true"
    className={`absolute inset-0 pointer-events-none
      bg-[linear-gradient(to_right,#D9D9D9_1px,transparent_1px),linear-gradient(to_bottom,#D9D9D9_1px,transparent_1px)]
      bg-[size:100px_100px] opacity-25 ${className}`}
  />
);

const Eyebrow = ({ children, className = 'text-[#D64545]' }) => (
  <p className={`font-mono text-[14px] sm:text-[16px] lg:text-[20px] font-medium tracking-[0.05em] uppercase ${className}`}>
    {children}
  </p>
);

/* ─── Ecosystem card decoration icon map ────────────────────── */
const ECOSYSTEM_ICONS = {
  cube:           '/icons/icon big (2).png',
  'platform-art': '/icons/icon big (3).png',
  squares:        '/icons/icon big (1).png',
  arrow:          '/icons/icon big.png',
};

/* ─── Ecosystem card ─────────────────────────────────────────── */
const EcosystemCard = ({ item }) => (
  <Link
    to={item.to}
    className="ecosystem-home-card relative flex flex-col overflow-hidden rounded-2xl border border-[#E0E0E0] bg-white p-2.5 sm:p-8 hover:shadow-md transition-shadow duration-300 cursor-default"
    style={{ minHeight: item.minHeight }}
  >
    <div className="arrow-trigger flex items-start justify-between gap-4 relative z-10 lg:cursor-pointer">
      <h3 className="font-sans text-[15px] sm:text-[28px] lg:text-[36px] font-medium text-[#D64545] max-w-[85%] leading-tight">
        {item.title}
      </h3>
      <span className="shrink-0 mt-1 cursor-pointer">
        <SlideArrow Icon={ArrowUpRight} iconClass="h-6 w-6 sm:h-9 sm:w-9 text-[#D64545]" strokeWidth={2} />
      </span>
    </div>

    <div className="relative z-10 flex flex-wrap gap-1.5 sm:gap-2 mt-auto pt-3 sm:pt-10">
      {item.tags.map((tag, i) => (
        <React.Fragment key={tag}>
          {item.lineBreakAt?.includes(i) && <span className="w-full h-0" />}
          <span className="rounded-full border border-[#D2D2D2] bg-[#EEEFF1] px-2.5 py-1 text-[11px] sm:px-4 sm:py-1.5 sm:text-[13px] font-medium text-[#000000] cursor-default">
            {tag}
          </span>
        </React.Fragment>
      ))}
    </div>

    {ECOSYSTEM_ICONS[item.decoration] && (
      <img
        src={ECOSYSTEM_ICONS[item.decoration]}
        alt=""
        aria-hidden="true"
        className={`block ${item.iconClass}`}
      />
    )}
  </Link>
);

/* ─── HomePage ───────────────────────────────────────────────── */
const HomePage = () => {
  const { setRef, isVisible } = useReveal();
  const [heroRef, heroOffset]             = useParallax(0.04);
  const [statImgRef, statOffset]          = useParallax(0.06);
  const { t } = useLanguage();

  const [logoErrors, setLogoErrors] = useState({});
  const handleLogoError = (name) => setLogoErrors((p) => ({ ...p, [name]: true }));

  const caseItems = [
    { Icon: Wifi,  label: t('home.case.connect'),   letter: 'C' },
    { Icon: Car,   label: t('home.case.autonomous'), letter: 'A' },
    { Icon: Users, label: t('home.case.shared'),     letter: 'S' },
    { Icon: Zap,   label: t('home.case.electric'),   letter: 'E' },
  ];

  const ecoProducts  = t('home.ecosystem.products');
  const ecoPlatform  = t('home.ecosystem.platform');
  const ecoDomains   = t('home.ecosystem.domains');
  const ecoServices  = t('home.ecosystem.services');

  const ecosystemLeft = [
    {
      title: ecoProducts.title, to: '/ecosystem?tab=products', tags: ecoProducts.tags, minHeight: 440, decoration: 'cube', lineBreakAt: [3],
      iconClass: 'absolute bottom-0 -right-2 w-[90px] h-[90px] sm:w-[390px] sm:h-[390px] sm:-right-14 object-contain opacity-100 pointer-events-none select-none',
    },
    {
      title: ecoDomains.title, to: '/ecosystem?tab=applications', tags: ecoDomains.tags, minHeight: 360, decoration: 'squares', lineBreakAt: [3],
      iconClass: 'absolute bottom-0 right-0 w-[85px] h-[85px] sm:w-80 sm:h-80 sm:-bottom-4 object-contain opacity-100 pointer-events-none select-none',
    },
  ];

  const ecosystemRight = [
    {
      title: ecoPlatform.title, to: '/ecosystem?tab=platform', tags: ecoPlatform.tags, minHeight: 320, decoration: 'platform-art', lineBreakAt: [2],
      iconClass: 'absolute bottom-0 right-0 w-[80px] h-[80px] sm:w-[250px] sm:h-[250px] sm:-right-4 object-contain opacity-100 pointer-events-none select-none',
    },
    {
      title: ecoServices.title, to: '/ecosystem?tab=services', tags: ecoServices.tags, minHeight: 480, decoration: 'arrow', lineBreakAt: [2],
      iconClass: 'absolute bottom-0 -right-2 w-[90px] h-[90px] sm:w-[400px] sm:h-[400px] sm:-right-16 object-contain opacity-100 pointer-events-none select-none',
    },
  ];

  const partners = [
    { name: 'F.C.C.',             label: 'Investor',                 logo: '/FCCLogo.png' },
    { name: 'Infineon',           label: 'Power Semiconductors',     logo: '/InfineonLogo.png' },
    { name: 'Texas Instruments',  label: 'Analog & Embedded',        logo: '/TexasInstrumentsLogo.png' },
    { name: 'NXP',                label: 'Automotive & IoT',         logo: '/NXPLogo.png' },
    { name: 'Microchip',          label: 'MCU & Analog',             logo: '/MicrochipLogo.png' },
    { name: 'onsemi',             label: 'Power & Sensing',          logo: '/OnsemiLogo.png' },
    { name: 'ROHM',               label: 'Power Management',         logo: '/ROHMLogo.png' },
    { name: 'STMicroelectronics', label: 'Analog & Power',           logo: '/StmicroelectronicsLogo.png' },
    { name: 'Renesas',            label: 'Microcontrollers & Power', logo: '/RenesasLogo.png' },
    { name: 'Altium',             label: 'PCB Design',               logo: '/AltiumLogo.png' },
    { name: 'Mathworks',          label: 'Simulation',               logo: '/MathworksLogo.png' },
    { name: 'PTC',                label: 'PLM Software',             logo: '/PTCLogo.png' },
    { name: 'Solidworks',         label: 'CAD Design',               logo: '/SolidworksLogo.png' },
    { name: 'Tektronix',          label: 'Test & Measurement',       logo: '/TektronixLogo.png' },
    { name: 'HIOKI',              label: 'Test & Measurement',       logo: '/HIOKILogo.png' },
  ];

  return (
    <div data-testid="home-page" className="overflow-hidden font-sans text-[#0F0F0F]">

      {/* ══════════════════════════════════════════════
          HERO
      ══════════════════════════════════════════════ */}
      <section className="relative bg-white overflow-hidden py-12 sm:py-0 sm:min-h-screen sm:flex sm:items-center">
        <GridBackdrop />

        <div
          ref={heroRef}
          style={{ transform: `translateY(${heroOffset}px)` }}
          className="absolute right-0 top-0 bottom-0 w-full sm:w-[65%] lg:w-[62%] max-w-4xl opacity-20 sm:opacity-60 lg:opacity-80 pointer-events-none select-none z-0"
          aria-hidden="true"
        >
          <img
            src="/HeroAnimation.gif"
            alt=""
            fetchpriority="high"
            className="w-full h-full object-cover"
            onError={(e) => { e.currentTarget.src = '/HeroAnimation.gif'; }}
          />
        </div>

        <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 w-full pt-14 sm:pt-0 sm:py-24 lg:py-20">
          <div className="max-w-[760px] fade-up-mount">
            <h1
              className="font-display font-bold leading-none text-[2.6rem] sm:text-[clamp(3rem,8vw,8rem)]"
              style={{ letterSpacing: '-0.02em', lineHeight: 1 }}
            >
              <span className="block text-gray-700 sm:whitespace-nowrap">{t('home.hero.line1')}</span>
              <span className="block text-[#D64545]">{t('home.hero.line2')}</span>
            </h1>

            <p className="mt-6 lg:mt-8 font-sans font-medium text-[#0F0F0F] text-[18px] sm:text-[18px] lg:text-[20px] leading-[1.4] max-w-[520px]">
              {t('home.hero.subtitle')}
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-5">
              <Link
                to="/ecosystem"
                className="inline-flex items-center justify-center bg-[#D64545] text-[#EEEFF1] font-sans font-semibold text-[14px] sm:text-[18px] px-5 py-2.5 sm:px-7 sm:py-4 hover:bg-[#BC2929] active:bg-[#0042B8] transition-colors duration-200"
              >
                {t('home.hero.explore')}
              </Link>

              <Link
                to="/contact"
                className="arrow-trigger cta-get-in-touch inline-flex items-center gap-1.5 font-sans font-medium text-[20px] sm:text-[18px]"
              >
                <span className="underline decoration-current underline-offset-4">{t('home.hero.getInTouch')}</span>
                <SlideArrow Icon={ArrowUpRight} iconClass="h-6 w-6 sm:h-5 sm:w-5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          INTRO — Art of Motion + CASE Mobility
      ══════════════════════════════════════════════ */}
      <section
        className="relative bg-[#EEEFF1] py-8 lg:py-20 overflow-hidden border-t border-[#D2D2D2]"
      >
        <GridBackdrop />
        <img
          src="/TakumiT.png"
          alt=""
          aria-hidden="true"
          className="absolute right-0 bottom-[-40px] w-[480px] h-auto object-contain opacity-[0.06] pointer-events-none select-none z-0"
        />

        {/* ── Art of Motion ── */}
        <div ref={setRef('artOfMotion')} className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid gap-8 lg:gap-12 lg:grid-cols-2 items-start reveal-left ${isVisible.artOfMotion ? 'reveal-in' : ''}`}>

            <div className="overflow-hidden rounded-[24px] border border-[#D2D2D2] bg-[#F1F2F6] aspect-[1.2/1]">
              <img
                src="https://images.pexels.com/photos/5973843/pexels-photo-5973843.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Japanese Craftsmanship — The Art of Motion"
                loading="lazy"
                width="940" height="650"
                className="w-full h-full object-cover grayscale"
              />
            </div>

            <div>
              <Eyebrow>{t('home.artOfMotion.eyebrow')}</Eyebrow>
              <h2 className="mt-4 font-display text-[28px] sm:text-[36px] lg:text-[48px] font-bold text-[#0042B8] leading-tight">
                {t('home.artOfMotion.heading')}
              </h2>
              <p className="mt-6 text-[16px] font-medium text-[#4A4A4A] leading-relaxed max-w-[480px]">
                {t('home.artOfMotion.description')}
              </p>
              <Link
                to="/about"
                className="mt-8 inline-flex items-center justify-center bg-[#D64545] text-[#EEEFF1] font-sans font-semibold text-[18px] px-8 py-3 hover:bg-[#BC2929] active:bg-[#0042B8] transition-colors duration-200"
              >
                {t('home.artOfMotion.cta')}
              </Link>
            </div>
          </div>
        </div>

        {/* ── CASE Mobility ── */}
        <div ref={setRef('case')} className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 mt-8 lg:mt-32">
          <div className={`reveal ${isVisible.case ? 'reveal-in' : ''}`}>
            <Eyebrow>{t('home.case.eyebrow')}</Eyebrow>
            <h2
              className="mt-4 font-display text-[28px] sm:text-[36px] lg:text-[48px] font-bold text-[#2C5AA0] mb-4 lg:mb-12"
              style={{ letterSpacing: '-0.02em' }}
            >
              {t('home.case.heading')}
            </h2>
          </div>

          <div className={`grid grid-cols-2 lg:grid-cols-4 gap-3 reveal ${isVisible.case ? 'reveal-in' : ''}`}>
            {caseItems.map(({ Icon, label, letter }, i) => (
              <div
                key={letter}
                className="relative overflow-hidden rounded-2xl border border-[#0042B8] bg-white p-4 sm:p-8 min-h-[160px] sm:min-h-[320px] lg:min-h-[420px] flex flex-col gap-2 sm:gap-5"
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <Icon className="h-8 w-8 sm:h-10 sm:w-10 lg:h-12 lg:w-12 text-[#0042B8]" strokeWidth={1.5} />
                <h3 className="font-sans text-[16px] sm:text-[30px] lg:text-[36px] font-medium text-[#0042B8] leading-tight">{label}</h3>
                <span
                  aria-hidden="true"
                  className="absolute bottom-0 left-[-10px] sm:left-[-20px] font-sans font-black text-[#0042B8] leading-none select-none pointer-events-none translate-y-[10%] sm:translate-y-[26%] lg:translate-y-[10%]"
                  style={{ fontSize: 'clamp(96px, 24vw, 380px)', letterSpacing: '-8px' }}
                >
                  {letter}
                </span>
              </div>
            ))}
          </div>

          <div className="flex justify-end mt-5 lg:mt-10">
            <p className="text-[16px] font-medium text-[#4A4A4A] max-w-[480px] text-left leading-relaxed">
              {t('home.case.description')}
            </p>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          ECOSYSTEM SECTION
      ══════════════════════════════════════════════ */}
      <section
        ref={setRef('ecosystem')}
        className="bg-[#EEEFF1] py-8 lg:py-20 border-t border-[#D2D2D2]"
      >
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`mb-6 lg:mb-14 reveal ${isVisible.ecosystem ? 'reveal-in' : ''}`}>
            <Eyebrow>{t('home.ecosystem.eyebrow')}</Eyebrow>
            <h2 className="mt-4 font-display text-[28px] sm:text-[36px] lg:text-[48px] font-semibold text-[#2C5AA0] leading-tight">
              {t('home.ecosystem.heading')}
            </h2>
            <p className="mt-5 text-[16px] font-medium text-[#000000] max-w-[580px] leading-relaxed">
              {t('home.ecosystem.description')}
            </p>
          </div>

          <div className={`grid gap-2 md:gap-5 md:grid-cols-2 reveal ${isVisible.ecosystem ? 'reveal-in' : ''}`}>
            <div className="flex flex-col gap-2 md:gap-5">
              {ecosystemLeft.map((item) => <EcosystemCard key={item.decoration} item={item} />)}
            </div>
            <div className="flex flex-col gap-2 md:gap-5">
              {ecosystemRight.map((item) => <EcosystemCard key={item.decoration} item={item} />)}
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          KNOW WHAT'S NEW
      ══════════════════════════════════════════════ */}
      <section
        ref={setRef('whatsNew')}
        className="relative bg-[#EEEFF1] py-8 lg:pt-20 lg:pb-20 border-t border-[#D2D2D2] overflow-hidden"
      >
        <GridBackdrop className="hidden lg:block" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`grid gap-8 lg:gap-12 lg:grid-cols-12 items-stretch reveal-right ${isVisible.whatsNew ? 'reveal-in' : ''}`}>

            <div
              ref={statImgRef}
              style={{ transform: `translateY(${statOffset}px)` }}
              className="lg:col-span-5 rounded-xl border border-[#0042B8] bg-[#ECECF2] overflow-hidden h-full min-h-[240px] sm:min-h-[360px] lg:min-h-0 self-stretch"
            >
              <img
                src="/TCUExploded.png"
                alt="TCU Exploded View"
                loading="lazy"
                className="w-full h-full object-contain mix-blend-multiply scale-[1.2] translate-y-[4%] sm:scale-[1.7] sm:translate-y-[5%]"
              />
            </div>

            <div className="lg:col-span-7 flex flex-col gap-6">
              <div>
                <Eyebrow className="text-[#D64545]">{t('home.whatsNew.eyebrow')}</Eyebrow>
                <h2 className="mt-3 font-display text-[28px] sm:text-[36px] lg:text-[48px] font-semibold text-[#2C5AA0] max-w-[480px] leading-tight">
                  {t('home.whatsNew.heading')}
                </h2>
                <p className="mt-4 text-[16px] font-medium text-black leading-relaxed max-w-[480px]">
                  {t('home.whatsNew.description')}
                </p>
              </div>

              <div className="flex flex-wrap gap-3 sm:gap-6">
                <div className="p-3">
                  <p className="font-sans text-[36px] font-medium text-black leading-tight">33 KW</p>
                  <p className="mt-1 text-[16px] font-normal text-black">{t('home.whatsNew.powerRange')}</p>
                </div>
                <div className="p-3">
                  <p className="font-sans text-[36px] font-medium text-black leading-tight">1200V</p>
                  <p className="mt-1 text-[16px] font-normal text-black">{t('home.whatsNew.voltageRange')}</p>
                </div>
              </div>

              <Link
                to="/ecosystem"
                className="mt-auto self-start sm:self-auto inline-flex items-center justify-center bg-[#D64545] text-[#EEEFF1] font-sans font-semibold text-[14px] sm:text-[18px] px-5 py-2.5 sm:px-7 sm:py-4 hover:bg-[#BC2929] active:bg-[#0042B8] transition-colors duration-200"
              >
                {t('home.whatsNew.cta')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          INVESTORS / PARTNERS BANNER
      ══════════════════════════════════════════════ */}
      <section className="relative bg-[#D64545] py-8 lg:py-10 text-center overflow-hidden">
        <div aria-hidden="true" className="hidden lg:block absolute inset-0 pointer-events-none bg-[linear-gradient(to_right,rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:100px_100px]" />
        <p className="relative z-10 font-sans text-[14px] sm:text-[18px] lg:text-[22px] font-semibold text-white flex items-center justify-center gap-4 sm:gap-8 lg:gap-10 flex-wrap px-4">
          <span>{t('home.partners.investors')}</span>
          <span className="w-[5px] h-[5px] rounded-full bg-white/60 inline-block" />
          <span>{t('home.partners.techPartners')}</span>
          <span className="w-[5px] h-[5px] rounded-full bg-white/60 inline-block" />
          <span>{t('home.partners.clients')}</span>
        </p>
      </section>

      {/* ══════════════════════════════════════════════
          PARTNER LOGO GRID
      ══════════════════════════════════════════════ */}
      <section className="relative bg-[#EEEFF1] py-8 lg:py-16 border-t border-[#D2D2D2] overflow-hidden">
        <GridBackdrop className="hidden lg:block" />
        <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-3 lg:grid-cols-5 gap-3">
            {partners.map((p, i) => (
              <div
                key={`${p.name}-${i}`}
                className="bg-[#F8F9FB] rounded-lg border border-[#0042B8]/40 p-2 sm:p-4 flex flex-col items-center justify-between text-center min-h-[90px] sm:min-h-[130px] hover:shadow-sm transition-shadow duration-300"
              >
                <div className="flex-1 flex items-center justify-center w-full">
                  {logoErrors[p.name] ? (
                    <span className="font-semibold text-sm text-[#4A4A4A]/60 uppercase tracking-wide">{p.name}</span>
                  ) : (
                    <img
                      src={p.logo}
                      alt={p.name}
                      onError={() => handleLogoError(p.name)}
                      className="max-h-7 sm:max-h-10 max-w-[70px] sm:max-w-[110px] object-contain mix-blend-multiply"
                    />
                  )}
                </div>
                <p className="mt-1 sm:mt-3 font-display text-[10px] sm:text-[13px] font-medium text-[#4A4A4A] tracking-wide">
                  {p.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════
          READY TO POWER YOUR VISION
      ══════════════════════════════════════════════ */}
      <section className="relative bg-[#EEEFF1] py-10 lg:py-24 overflow-hidden border-t border-[#D2D2D2]">
        <GridBackdrop className="hidden lg:block" />
        <img
          src="/Whiplash T line.png"
          alt=""
          aria-hidden="true"
          className="block absolute right-[-8%] lg:right-0 top-0 h-[150%] lg:h-[160%] w-auto object-contain pointer-events-none select-none"
          style={{ filter: 'brightness(0) saturate(100%) invert(36%) sepia(94%) saturate(651%) hue-rotate(322deg) brightness(105%)' }}
        />

        <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-[clamp(2rem,5vw,4rem)] font-bold text-[#D64545] leading-tight">
            {t('home.cta.heading')}
          </h2>
          <p className="mt-6 text-[20px] font-medium text-[#4A4A4A] max-w-[480px] leading-[1.4]">
            {t('home.cta.description')}
          </p>
          <div className="mt-6 lg:mt-10">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center border border-[#0F0F0F] px-8 py-3 text-[18px] font-semibold font-sans text-[#0F0F0F] hover:bg-[#D64545] hover:text-black hover:border-[#D64545] active:bg-[#0042B8] active:text-white active:border-[#0042B8] transition-all duration-300"
            >
              {t('home.cta.button')}
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
};

export default HomePage;
